import React, { useState, useEffect } from "react";
import LoadingIndicator from "../common/loading_indicator.jsx";
import { Row, Col, Button } from "../bootstrap/index.jsx";
import Grid from "./Grid.jsx";
import { Adalicon } from "../common/Icons.jsx";
import update from "immutability-helper";
import ToggleSwitch, { TextOptionElem } from "../common/ToggleSwitch.jsx";
import SummaryTable from "./SummaryTable.jsx";
import { castDataType, DataType } from "../form_editor/common.jsx";
import { Parser } from 'hot-formula-parser';
import { parseFormula, sortFormulasBasedOnDependencies } from "../../helpers/index.js";
import ExcelExportButton from "./ExcelExportButton.jsx";

const BudgetingGridContainer = ({
  rows: initialRows,
  readOnly,
  onUpdate = () => {},
  afterInit = () => {},
  fullWidth = true,
  formMode,
  showSummaryTable,
  columns,
  calculateAggregateColumnFormula, stateKey,
  hidden = false,
  dimensions,
  selectedDimensions,
  groupByColumn = null
}) => {
  const [rows, setRows] = useState(initialRows || []);
  const tableContainerRef = React.useRef();
  const [viewMode, setViewMode] = useState(!["fill", "edit"].includes(formMode));
  const [wrapperHeight, setWrapperHeight] = React.useState(40);

  const addRow = () => {
    setRows(rows.concat([{ id: rows.length == 0 ? 0 : Math.max(...rows.map((r) => r.id)) + 1 }]));
  };

  const deleteRow = (id) => {
    if (confirm(I18n.t("confirm_delete_budget_row"))) {
      setRows(rows.filter((r) => r.id != id));
    }
  };

  const updateRowCalculations = (origRow) => {
    let row = { ...origRow };

    // Use all columns as param because non-calculation columns will be dependencies for calculation columns
    sortFormulasBasedOnDependencies(Parser, columns).forEach(({ formula, type, strId }) => {
      // Filtering calculation columns here for calculations
      if (type == "calculation" && formula) {
        let variableValues = {};

        columns.forEach((c) => {
          // Use given value for col or columns's default value if that is not present.
          const val = castDataType(row[c.strId] || c.value, DataType.DECIMAL);
          if (val || val === 0) {
            variableValues[c.key] = val;
          }
        });

        const { result, error } = parseFormula(Parser, formula, variableValues);
        row[strId] = result;
        row[`${strId}-error`] = error;
      }
    });

    return row;
  };

  const getColumnAggregateValue = (values, row, column) =>
    Object.fromEntries(
      columns
        .filter((col) => col.key && col.type == "input")
        .map((col) => [col.strId, row.values[col.strId]])
    );

  const updateAllRowsCalculations = () => {
    const newRows = update(rows, (arr) => arr.map((row) => updateRowCalculations(row)));
    setRows(newRows);
    afterInit(newRows);
  };

  useEffect(() => {
    // Update calculations for all rows on mount
    updateAllRowsCalculations();
  }, []);

  const updateRow = (id, attr, value, updateCalculationColumns) => {
    const index = rows.findIndex((r) => r.id == id);
    let row = { ...rows[index], [attr]: value };

    if (updateCalculationColumns) {
      row = updateRowCalculations(row);
    }

    setRows(
      update(rows, {
        [index]: {
          $set: row,
        },
      })
    );
  };

  useEffect(() => {
    onUpdate(rows);
  }, [rows]);


  const handleResize = (height) => {
    setWrapperHeight(height);
  }

  if (hidden) {
    return null;
  }

  return !dimensions ? (
    <LoadingIndicator size="sm" />
  ) : (
    <>
      <div style={{ width: 80, display: "block", float: "right" }} className="mb-1">
        <ToggleSwitch
          name={`preview-toggle-${Math.random() * 100}`}
          isOn={viewMode}
          handleToggle={() => {
            setViewMode(!viewMode);
          }}
          yesElem={<TextOptionElem text={I18n.t("preview")} className="ml-1" />}
          noElem={<TextOptionElem text={I18n.t("fill")} side="right" className="mr-1" />}
        />
      </div>
      <Row>
        { viewMode && <ExcelExportButton columns={columns} /> }
        <Col>
          <div
            style={{height: wrapperHeight}} ref={tableContainerRef}
            className={`react-table-container striped-groups budget-table ${fullWidth ? "" : "non-full-width"}`}
          >
            <Grid
              dimensions={dimensions}
              rows={rows}
              columns={columns}
              readOnly={readOnly}
              deleteRow={deleteRow}
              updateRow={updateRow}
              // fullWidth={fullWidth}
              viewMode={viewMode}
              onClientHeightChange={handleResize}
              calculateAggregateColumnFormula={calculateAggregateColumnFormula}
              getColumnAggregateValue={getColumnAggregateValue}
              stateKey={stateKey}
              selectedDimensions={selectedDimensions}
              groupByColumn={groupByColumn}
            />
          </div>
        </Col>
      </Row>
      {!viewMode && (
        <Row className="mt-1">
          <Col>
            <Button elemType="button" disabled={readOnly} onClick={addRow}>
              <Adalicon type="add" /> {I18n.t("add_row", { scope: "fe" })}
            </Button>
          </Col>
        </Row>
      )}
      {viewMode && showSummaryTable && dimensions.length>0 && dimensions.find((d) => d.sort_order == parseInt(showSummaryTable, 10)) && (
        <div
          className={`mt-2 react-table-container no-stripes budget-summary-table ${fullWidth ? "" : "non-full-width"}`}
        >
          <SummaryTable
            rows={rows}
            dimension={dimensions.find((d) => d.sort_order == parseInt(showSummaryTable, 10))}
            fullWidth={fullWidth}
            columns={groupByColumn == null ? columns : columns.filter((c) => c.key != groupByColumn)}
            calculateAggregateColumnFormula={calculateAggregateColumnFormula}
            getColumnAggregateValue={getColumnAggregateValue}
            stateKey={stateKey + '_summary'}
          />
        </div>
      )}
    </>
  );
};

export default BudgetingGridContainer;
